import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { 
  FiRadio, FiGlobe, FiClock, FiHeart, FiZap, 
  FiMusic, FiDownload, FiShield, FiSliders, FiVolume2 
} from 'react-icons/fi';
import MicrosoftStoreBtn from '../components/MicrosoftStoreBtn';
import { getProduct } from '../data/catalog';
import Reel from '../components/Reel';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-card border border-border p-6 rounded-none hover:border-accent-blue/50 transition-all group">
    <div className="w-12 h-12 rounded-md bg-accent-blue/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={icon} className="w-6 h-6 text-accent-blue" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-muted text-sm leading-relaxed">{desc}</p>
  </div>
);

const RadioVerse = () => {
  const PRODUCT = getProduct('radioverse');

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-blue/10 border border-accent-blue/20 text-accent-blue text-sm font-medium mb-6">
              <SafeIcon icon={FiRadio} /> The Ultimate Radio Experience for Windows
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
              Global Frequencies,<br />
              <span className="text-accent-blue">Native Performance</span>
            </h1>
            <p className="text-xl text-muted max-w-3xl mx-auto mb-10 leading-relaxed">
              Explore over 50,000 radio stations from every corner of the globe. 
              RadioVerse is a lightweight, ad-free, and privacy-focused player designed for Windows 10 & 11.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <MicrosoftStoreBtn 
                accentColor="bg-accent-blue" 
                className="px-10 py-4 scale-110" 
                href={PRODUCT.storeLink}
              />
              <div className="flex items-center gap-2 text-sm text-muted">
                <SafeIcon icon={FiShield} className="text-accent-blue" />
                <span>{PRODUCT.priceNote}. No account required.</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Reel — the frames for this product, so a visitor sees what it does. */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — RadioVerse</p>
          <Reel frames={PRODUCT.frames} label="RadioVerse" sound={false} ratio="aspect-[16/10]" />
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Pure Listening Pleasure</h2>
            <p className="text-muted">Features designed to make your radio experience seamless and enjoyable.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard 
              icon={FiGlobe} 
              title="50,000+ Stations" 
              desc="Browse by country, language, or genre. From local news to underground techno." 
            />
            <FeatureCard 
              icon={FiZap} 
              title="Instant Play" 
              desc="High-performance streaming engine with minimal buffering and ultra-low latency." 
            />
            <FeatureCard 
              icon={FiClock} 
              title="Sleep Timer" 
              desc="Fall asleep to your favorite station with an integrated, customizable timer." 
            />
            <FeatureCard 
              icon={FiHeart} 
              title="Favorites Sync" 
              desc="Keep your top stations organized and accessible with a single click." 
            />
          </div>
        </div>
      </section>

      {/* Technical Excellence */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-card to-background border border-border rounded-[2rem] p-8 md:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-accent-blue/5 rounded-full blur-3xl -z-10" />
            
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold text-white mb-8">Audio Quality & <span className="text-accent-blue">Privacy</span></h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-accent-blue/10 flex items-center justify-center shrink-0">
                      <SafeIcon icon={FiVolume2} className="text-accent-blue w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-lg">High-Fidelity Streams</h4>
                      <p className="text-muted">Automatic selection of the highest quality stream available for every station.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-accent-blue/10 flex items-center justify-center shrink-0">
                      <SafeIcon icon={FiSliders} className="text-accent-blue w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-lg">Custom URLs</h4>
                      <p className="text-muted">Can't find a station? Just paste the stream URL and add it to your library instantly.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-accent-blue/10 flex items-center justify-center shrink-0">
                      <SafeIcon icon={FiShield} className="text-accent-blue w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-lg">No Tracking</h4>
                      <p className="text-muted">We believe your listening habits are private. RadioVerse collects zero telemetry.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background/50 border border-border rounded-none p-8">
                <div className="flex items-center gap-3 mb-8">
                   <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                   <span className="text-sm font-medium text-white uppercase tracking-widest">App Specifications</span>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between py-2 border-b border-border/50 text-sm">
                    <span className="text-muted">OS Compatibility</span>
                    <span className="text-white">Windows 10 / 11</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/50 text-sm">
                    <span className="text-muted">Audio Formats</span>
                    <span className="text-white">MP3, AAC, OGG, HLS</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/50 text-sm">
                    <span className="text-muted">Memory Usage</span>
                    <span className="text-white">&lt; 100 MB RAM</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/50 text-sm">
                    <span className="text-muted">Background Play</span>
                    <span className="text-white">Fully Supported</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/50 text-sm">
                    <span className="text-muted">Price</span>
                    <span className="text-white font-bold">{PRODUCT.price}</span>
                  </div>
                </div>
                <div className="mt-8">
                  <MicrosoftStoreBtn 
                    accentColor="bg-accent-blue" 
                    className="w-full" 
                    href={PRODUCT.storeLink}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-white mb-6">Tune in to the world.</h2>
          <p className="text-muted mb-10">
            Join thousands of listeners who enjoy a cleaner, faster, and more private radio experience on Windows.
          </p>
          <div className="flex flex-col items-center gap-4">
            <MicrosoftStoreBtn 
              accentColor="bg-accent-blue" 
              className="px-12 py-5 scale-110" 
              href={PRODUCT.storeLink}
            />
            <span className="text-xs text-muted">One-time purchase on the Microsoft Store</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RadioVerse;